import { Path, useForm } from "react-hook-form";
import { Box } from "@mui/material";
import FormRow from "components/form/FormRow";
import { MESSAGE, REGEX } from "constants/validate";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import CancelButton from "components/button/CancelButton";
import ButtonComponent from "components/button/ButtonComponent";
import { formatDateTime } from "utils";
import React, { useEffect } from "react";
import { Tire } from "../types/tire";

const styles = {
  field: {
    width: "100%",
    height: "48px",
    boxSizing: "border-box",
    backgroundColor: "var(--white)",
    borderRadius: 0,
    border: "3px solid var(--silver-150)",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
  closeBtn: {},
};

interface ITireFormProps {
  type: "update" | "create";
  visible: boolean;
  onClose: () => void;
  onHandleSubmit: (form: Tire) => void;
  isLoading: boolean;
  tireDetail: Tire;
}

const tireFormFieldsReadOnly = [
  { label: "YH商品コード", disabled: true, fieldName: "item_cd" },
  { label: "JANコード", disabled: true, fieldName: "jan_cd" },
  { label: "ブランドコード", disabled: true, fieldName: "brand_cd" },
  { label: "商品名", disabled: true, fieldName: "item_name" },
  { label: "メーカー品番", disabled: true, fieldName: "maker_item_cd" },
  { label: "サイズ", disabled: true, fieldName: "item_size" },
  { label: "大分類", disabled: true, fieldName: "item_category_l" },
  { label: "中分類", disabled: true, fieldName: "item_category_m" },
  { label: "小分類", disabled: true, fieldName: "item_category_s" },
  { label: "定価", disabled: true, fieldName: "list_price" },
  { label: "A表価格", disabled: true, fieldName: "a_price" },
  { label: "メイン仕入先コード", disabled: true, fieldName: "main_vendor_cd" },
  { label: "メイン仕入先原価", disabled: true, fieldName: "main_purchase_cost" },
  { label: "発注先コード", disabled: true, fieldName: "shipping_dest_cd" },
  {
    label: "廃番",
    disabled: true,
    fieldName: "waste_num_flag",
    type: "select",
    options: [
      { label: "有効", value: 1 },
      { label: "無効", value: 0 },
    ],
  },
  { label: "", disabled: true, fieldName: "none_block_0" },
  {
    label: "ECフラグ",
    disabled: true,
    fieldName: "ec_flag",
    type: "select",
    options: [
      { label: "有効", value: 1 },
      { label: "無効", value: 0 },
    ],
  },
  { label: "2次仕入先コード", disabled: true, fieldName: "vendor_cd_2nd" },
  { label: "3次仕入先コード", disabled: true, fieldName: "vendor_cd_3rd" },
  { label: "発注単位", disabled: true, fieldName: "hatchu_unit" },
  { label: "", disabled: true, fieldName: "none_block_1" },
];

const tireFormFieldsEdit = [
  { label: "キャッシュバック設定", fieldName: "cashback_setting", type: "label" },
  {
    label: "タイヤ4本",
    fieldName: "cb_tire_price",
    rules: {
      required: MESSAGE.EMPTY,
      pattern: {
        value: REGEX.NUMERIC_FORMAT(9, 0),
        message: MESSAGE.NUMERIC_FORMAT(9, 0),
      },
    },
    type: "number",
  },
  {
    label: "ホイール4本",
    fieldName: "cb_wheel_price",
    rules: {
      required: MESSAGE.EMPTY,
      pattern: {
        value: REGEX.NUMERIC_FORMAT(9, 0),
        message: MESSAGE.NUMERIC_FORMAT(9, 0),
      },
    },
    type: "number",
  },
  { label: "サイズ設定", fieldName: "size_setting", type: "label", sxBoxRow: { mt: 3 } },
  {
    label: "幅",
    fieldName: "tire_width",
    rules: {
      pattern: {
        value: REGEX.NUMERIC_FORMAT(9, 0),
        message: MESSAGE.NUMERIC_FORMAT(9, 0),
      },
    },
    type: "number",
  },
  {
    label: "扁平率",
    fieldName: "tire_aspect_ratio",
    rules: {
      required: MESSAGE.EMPTY,
      pattern: {
        value: REGEX.NUMERIC_FORMAT(9, 0),
        message: MESSAGE.NUMERIC_FORMAT(9, 0),
      },
    },
    type: "number",
  },
  {
    label: "リム径",
    fieldName: "tire_rim_diam",
    rules: {
      required: MESSAGE.EMPTY,
      pattern: {
        value: REGEX.NUMERIC_FORMAT(3, 1),
        message: MESSAGE.NUMERIC_FORMAT(3, 1),
      },
      type: "number-float",
    },
  },
  {
    label: "速度記号",
    fieldName: "tire_loin_speed_mark",
    rules: {
      required: MESSAGE.EMPTY,
      maxLength: {
        message: MESSAGE.MAX_LENGTH(4),
        value: 4,
      },
      type: "text",
    },
  },
  {
    label: "ラベル",
    fieldName: "tire_label",
    rules: {
      maxLength: {
        message: MESSAGE.MAX_LENGTH(10),
        value: 10,
      },
    },
    sxBoxRow: { mb: 3 },
  },
  {
    label: "タイヤカテゴリ",
    fieldName: "tire_category",
    type: "select",
    options: [
      { value: "s", label: "サマー" },
      { value: "w", label: "スタッドレス" },
      { value: "a", label: "オールシーズン" },
    ],
  },
  {
    label: "オススメ",
    fieldName: "recommend_flag",
    type: "select",
    options: [
      { label: "はい", value: 1 },
      { label: "いいえ", value: 0 },
    ],
  },
  {
    label: "有効/無効",
    fieldName: "status_flag",
    type: "select",
    options: [
      { label: "有効", value: 1 },
      { label: "無効", value: 0 },
    ],
  },
];

/**
 * TireFormModal Component
 *
 * Modal form for creating or updating wheel details.
 * Uses react-hook-form for form state management and validation.
 * Displays read-only and editable fields, image preview, and action buttons.
 * @param {object} props - Component props.
 * @param {"update" | "create"} [props.type] - Modal mode, either update or create.
 * @param {boolean} props.visible - Whether the modal is visible.
 * @param {() => void} props.onClose - Callback to close the modal.
 * @param {(form: Tire) => void} props.onHandleSubmit - Callback for form submission.
 * @param {boolean} props.isLoading - Loading state for the form.
 * @param {Tire} props.tireDetail - Tire detail data for the form.
 */
export default function TireFormModal({
  type = "update",
  visible,
  onClose,
  onHandleSubmit,
  isLoading,
  tireDetail,
}: ITireFormProps) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Tire>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      ...tireDetail,
      tire_label: tireDetail.tire_label || "",
      ec_flag: !tireDetail.ec_flag ? 0 : 1,
    },
  });

  const isUpdate = type === "update";

  const onSubmit = (data: Tire) => {
    if (isLoading || Object.keys(errors).length > 0) return;
    const requestData: Tire = {
      ...data,
      tire_label: data.tire_label || "",
      remarks: data.remarks || "",
    };
    onHandleSubmit(requestData);
  };

  useEffect(() => {
    if (tireDetail && tireDetail.id) {
      reset({
        ...tireDetail,
        ec_flag: !tireDetail.ec_flag ? 0 : 1,
      });
    }
  }, [tireDetail?.id]);

  return (
    <Modal
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      open={visible}
      onClose={onClose}
    >
      <Box
        sx={{
          maxHeight: "90vh",
          overflow: "auto",
          width: "80%",
          backgroundColor: "var(--white)",
          padding: { xs: 3, md: 4, xl: "30px 60px" },
          maxWidth: { xs: "90%", lg: "1300px" },
          borderRadius: "21px",
          display: "flex",
          flexDirection: "column",
          rowGap: "20px",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="10px">
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: "fontWeightMedium",
              color: "var(--gray-700)",
              fontSize: { xs: 28, md: 33 },
            }}
          >
            商品詳細
            <Typography component="br" sx={{ display: { xs: "block", md: "none" } }} />
            {isUpdate && (
              <Typography
                component="span"
                sx={{
                  fontSize: { xs: 16, md: 21 },
                  fontWeight: "fontWeightMedium",
                  color: "var(--gray-700)",
                  ml: { xs: 0, md: 3 },
                }}
              >
                最終更新日 {formatDateTime(tireDetail?.updated_at || "")}
              </Typography>
            )}
          </Typography>
          <CancelButton onClose={onClose} />
        </Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
            <Box sx={{ width: { xs: "100%", md: "calc(60% - 24px)" } }}>
              {tireFormFieldsReadOnly.map((el) => (
                <FormRow
                  key={el.fieldName}
                  sxLabel={{ pl: 4, width: { xs: "40%", md: "45%" } }}
                  label={el.label}
                  sxBoxField={{ width: { xs: "60%", md: "55%" }, flexGrow: 1 }}
                  sxField={styles.field}
                  name={el.fieldName as Path<Tire>}
                  type={el.type as never}
                  control={control}
                  options={el.type === "select" ? el.options : undefined}
                  disabled={el.disabled}
                />
              ))}
            </Box>
            <Box sx={{ width: { xs: "100%", md: "40%" } }}>
              {tireFormFieldsEdit.map((el) => (
                <FormRow
                  key={el.fieldName}
                  sxLabel={{ pl: 4, width: "40%" }}
                  label={el.label}
                  sxBoxField={{ width: "60%", flexGrow: 1 }}
                  sxBoxRow={el.sxBoxRow}
                  sxField={styles.field}
                  name={el.fieldName as Path<Tire>}
                  type={el.type as never}
                  control={control}
                  rules={el.rules ? el.rules : undefined}
                  options={el.type === "select" ? el.options : undefined}
                />
              ))}
            </Box>
          </Box>

          <FormRow
            key="remarks"
            sxLabel={{ pl: 4, width: { xs: "40%", md: "calc(((60% - 24px) * 45)/100)" } }}
            label="コメント"
            sxBoxField={{ width: { xs: "60%", md: "55%" }, flexGrow: 1 }}
            sxField={styles.field}
            sxBoxRow={{ mt: 3 }}
            name="remarks"
            control={control}
            rules={{ maxLength: { value: 256, message: MESSAGE.MAX_LENGTH(256) } }}
          />

          <ButtonComponent
            disabled={isLoading || Object.keys(errors).length > 0}
            type="submit"
            label="更新"
            theme="dark"
            sx={{ height: "50px", minWidth: "185px", width: "28%", maxWidth: 239, mt: 2.5 }}
          />
          <Typography
            sx={{ fontSize: { xs: 16, md: 21 }, mt: 2.5 }}
            fontWeight="medium"
            color="var(--gray-700)"
          >
            更新日 {formatDateTime(tireDetail.updated_at)}
          </Typography>
        </form>
        <CancelButton sx={{ mt: 1.5 }} onClose={onClose} />
      </Box>
    </Modal>
  );
}
